<template>
  <div>
    <!-- ローディング中 -->
    <div v-if="loading" class="flex items-center justify-center py-16" role="status" aria-live="polite">
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-4 border-q4cl-border border-t-q4cl-accent mx-auto mb-4" aria-hidden="true"></div>
        <p class="text-q4cl-muted text-sm">{{ $t('問診票を読み込んでいます...') }}</p>
      </div>
    </div>

    <!-- エラー -->
    <div v-else-if="error" class="max-w-md mx-auto mt-8">
      <q4cl-card>
        <h1 class="text-lg font-bold text-q4cl-danger mb-2 flex items-center gap-2">
          <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
          <span>{{ $t('読み込みに失敗しました') }}</span>
        </h1>
        <p class="text-sm text-q4cl-ink whitespace-pre-line">{{ error }}</p>
        <div class="mt-4 flex gap-2">
          <button type="button" class="btn-primary" @click="reload">{{ $t('再読込') }}</button>
          <router-link :to="{ path: '/usage', query: $route.query }" class="btn-secondary no-underline">{{ $t('使い方を見る') }}</router-link>
        </div>
      </q4cl-card>
    </div>

    <!-- 問診票表示 (hash/debug 無しは bootstrap 内で /usage へ redirect 済みなので分岐不要) -->
    <questionnaire-form
      v-else-if="qstDef"
      :qst-def="qstDef"
      :user-property="userProperty"
      :user-id="userId"
      :q-name="qName"
      :public-key="publicKey"
      :answer-id="answerId"
      :mail-notification="mailNotification"
      :debug="isDebug"
    />
  </div>
</template>

<script>
import Q4clCard from '@/components/ui/Q4clCard.vue'
import QuestionnaireForm from '@/components/QuestionnaireForm.vue'
export default {
  name: 'Home',
  components: {
    'q4cl-card': Q4clCard,
    'questionnaire-form': QuestionnaireForm
  },
  setup() {
    const route = VueRouter.useRoute();
    const router = VueRouter.useRouter();
    const loading = Vue.ref(false);
    const error = Vue.ref('');
    const hash = Vue.ref(route.query.hash || null);
    const isDebug = Vue.ref(String(route.query.debug || '') === 'true');

    const qstDef = Vue.ref(null);
    const userProperty = Vue.ref(null);
    const userId = Vue.ref('');
    const qName = Vue.ref('');
    const publicKey = Vue.ref(null);
    const answerId = Vue.ref('');
    const mailNotification = Vue.ref(false);

    async function loadDebug() {
      if (!window.DEBUG_QST_DEF) {
        await new Promise((res, rej) => {
          const s = document.createElement('script');
          s.src = 'js/views/debugQstDef.js';
          s.onload = res;
          s.onerror = rej;
          document.head.appendChild(s);
        });
      }
      qstDef.value = window.DEBUG_QST_DEF;
      userProperty.value = {
        name: 'デバッグクリニック',
        welcomeMessage: 'デバッグモードです。すべてのフィールド型をテストできます。',
        endingMessage: 'デバッグモード送信処理を完了しました。',
        privacyPolicy: '# プライバシーポリシー\n\nテスト用のプライバシーポリシーです。\n\n- 個人情報は暗号化して送信されます\n- 内部リンクテスト: [#/usage への内部リンク](#/usage)\n- 外部リンクテスト: [Emuyn 公式](https://www.emuyn.net/)',
        privacyItemsRequired: [],
        mailNotification: false,
        // 施設詳細モーダル検証 (項目 2.7 — showAbout で表示される)
        representative: '山田 太郎',
        zipcode: '100-0001',
        address: '東京都千代田区千代田 1-1 デバッグビル 3F',
        mail: 'debug@example.com',
        tel1: '03-1234-5678',
        tel2: '03-1234-5679',
        fax: '03-1234-5680',
        url: 'https://debug-clinic.example.com/',
        // 試用期間警告検証 (項目 2.1 — feewaiver なし → 「試用期間中」 マーカー表示)
        // 「無償ライセンス」 表示を試す場合は subscription: { feewaiver: true } に変更
        subscription: {}
      };
      userId.value = 'debug_user';
      qName.value = qstDef.value.title;
      publicKey.value = null;
      answerId.value = window.questionnaireStore.generateAnswerId();
      mailNotification.value = false;
    }

    async function loadByHash() {
      const state = await window.questionnaireStore.loadByHash(hash.value);
      qstDef.value = state.qstDef;
      userProperty.value = state.userProperty;
      userId.value = state.userId;
      qName.value = state.qName;
      publicKey.value = state.publicKey;
      answerId.value = state.answerId;
      mailNotification.value = state.mailNotification;
    }

    async function bootstrap() {
      // hash も debug も無いときは /usage へ redirect (案内画面廃止)
      if (!isDebug.value && !hash.value) {
        router.replace({ name: 'usage' });
        return;
      }
      loading.value = true;
      error.value = '';
      try {
        if (isDebug.value) await loadDebug();
        else if (hash.value) await loadByHash();
      } catch (e) {
        console.error('[Home] load error:', e);
        error.value = (e && e.message) || String(e);
      } finally {
        loading.value = false;
      }
    }

    Vue.onMounted(bootstrap);
    Vue.watch(() => route.query, (q) => {
      const newHash = q.hash || null;
      const newDebug = String(q.debug || '') === 'true';
      // 値が変わらず既にロード済みなら再ロード不要 (例: /usage との往復)
      if (newHash === hash.value && newDebug === isDebug.value && qstDef.value) return;
      hash.value = newHash;
      isDebug.value = newDebug;
      bootstrap();
    });

    function reload() { bootstrap(); }

    return {
      loading, error, hash, isDebug,
      qstDef, userProperty, userId, qName, publicKey, answerId, mailNotification,
      reload
    };
  }
};
</script>
